import { Grid, IconButton, Paper } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import React, { useContext, useEffect, useState } from "react";
import PageContainer from "../containers/PageContainer";
import PageTitleCard from "../cards/PageTitleCard";
import PageTitleText from "../texts/PageTitleText";
import { useTranslation } from "react-i18next";
import useFetch from "../hooks/useFetch";
import UserContext from "../contexts/UserContext";
import {
    getUserStatistics,
    getMenuItemStatistics,
    getHourlyStatistics,
    getUtmSourceStatistics,
} from "../config/apiRoutes";
import RefreshIcon from '@mui/icons-material/Refresh';
import { DailyScreenViews, MenuItemClicks, HourlyViews, UtmSources } from "../models/Statistics";
import UserTrafficChart from "../statistics/UserTrafficChart";
import HourlyTrafficChart from "../statistics/HourlyTrafficChart";
import UtmSourceChart from "../statistics/UtmSourceChart";
import KpiCards from "../statistics/KpiCards";
import SnackbarContext from "../contexts/SnackbarContext";
import BigLoader from "../common/BigLoader";
import PrimaryButtonGroup from "../buttons/PrimaryButtonGroup";
import { hasRole } from "../models/User";
import { RoleType } from "../models/Role";
import PrimaryButton from "../buttons/PrimaryButton";
import { useNavigate, useParams } from "react-router";
import { paymentSettingsRoute } from "../config/pageRoutes";
import MenuLikeStatisticTable from "../statistics/MenuLikeStatisticTable";
import ClickCountTable from "../statistics/ClickCountTable";

type Period = 'last7days' | 'last30days';

const DUMMY_PAGE_VIEWS: DailyScreenViews = {
    last7days: [
        { date: '2026-03-11', count: 145 }, { date: '2026-03-12', count: 203 },
        { date: '2026-03-13', count: 178 }, { date: '2026-03-14', count: 267 },
        { date: '2026-03-15', count: 312 }, { date: '2026-03-16', count: 289 },
        { date: '2026-03-17', count: 341 },
    ],
    last30days: [
        { date: '2026-02-15', count: 178 }, { date: '2026-02-16', count: 145 },
        { date: '2026-02-17', count: 203 }, { date: '2026-02-18', count: 234 },
        { date: '2026-02-19', count: 312 }, { date: '2026-02-20', count: 289 },
        { date: '2026-02-21', count: 156 }, { date: '2026-02-22', count: 167 },
        { date: '2026-02-23', count: 198 }, { date: '2026-02-24', count: 234 },
        { date: '2026-02-25', count: 267 }, { date: '2026-02-26', count: 301 },
        { date: '2026-02-27', count: 289 }, { date: '2026-02-28', count: 178 },
        { date: '2026-03-01', count: 145 }, { date: '2026-03-02', count: 189 },
        { date: '2026-03-03', count: 223 }, { date: '2026-03-04', count: 256 },
        { date: '2026-03-05', count: 298 }, { date: '2026-03-06', count: 312 },
        { date: '2026-03-07', count: 187 }, { date: '2026-03-08', count: 167 },
        { date: '2026-03-09', count: 201 }, { date: '2026-03-10', count: 234 },
        { date: '2026-03-11', count: 145 }, { date: '2026-03-12', count: 203 },
        { date: '2026-03-13', count: 178 }, { date: '2026-03-14', count: 267 },
        { date: '2026-03-15', count: 312 }, { date: '2026-03-16', count: 289 },
    ],
    prev7days: [
        { date: '2026-03-04', count: 120 }, { date: '2026-03-05', count: 165 },
        { date: '2026-03-06', count: 142 }, { date: '2026-03-07', count: 198 },
        { date: '2026-03-08', count: 245 }, { date: '2026-03-09', count: 221 },
        { date: '2026-03-10', count: 278 },
    ],
    prev30days: [],
};

const DUMMY_HOURLY_VIEWS: HourlyViews = {
    last7days: [
        { hour: 8, count: 45 }, { hour: 9, count: 78 }, { hour: 10, count: 112 },
        { hour: 11, count: 189 }, { hour: 12, count: 312 }, { hour: 13, count: 289 },
        { hour: 14, count: 198 }, { hour: 15, count: 134 }, { hour: 16, count: 145 },
        { hour: 17, count: 167 }, { hour: 18, count: 234 }, { hour: 19, count: 278 },
        { hour: 20, count: 256 }, { hour: 21, count: 198 }, { hour: 22, count: 112 },
        { hour: 23, count: 67 },
    ],
    last30days: [
        { hour: 8, count: 190 }, { hour: 9, count: 345 }, { hour: 10, count: 478 },
        { hour: 11, count: 823 }, { hour: 12, count: 1342 }, { hour: 13, count: 1198 },
        { hour: 14, count: 867 }, { hour: 15, count: 578 }, { hour: 16, count: 623 },
        { hour: 17, count: 712 }, { hour: 18, count: 989 }, { hour: 19, count: 1123 },
        { hour: 20, count: 1089 }, { hour: 21, count: 834 }, { hour: 22, count: 478 },
        { hour: 23, count: 289 },
    ],
};

const DUMMY_UTM_SOURCES: UtmSources = {
    last7days: [
        { source: 'qr_code', count: 1245 }, { source: '(direct)', count: 534 },
        { source: 'google', count: 178 }, { source: 'facebook', count: 89 },
    ],
    last30days: [
        { source: 'qr_code', count: 5234 }, { source: '(direct)', count: 2134 },
        { source: 'google', count: 734 }, { source: 'facebook', count: 389 },
        { source: 'instagram', count: 201 },
    ],
};

const StatisticsPage: React.FC = () => {
    const { t } = useTranslation();
    const { lang } = useParams();
    const { selectedRestaurant, user, hasFeature } = useContext(UserContext);
    const snackbar = useContext(SnackbarContext);
    const navigate = useNavigate();

    const [period, setPeriod] = useState<Period>('last30days');
    const [pageViewData, setPageViewData] = useState<DailyScreenViews | null>(null);
    const [menuClickData, setMenuClickData] = useState<MenuItemClicks | null>(null);
    const [hourlyData, setHourlyData] = useState<HourlyViews | null>(null);
    const [utmData, setUtmData] = useState<UtmSources | null>(null);

    const pageViewCaller = useFetch();
    const menuClickCaller = useFetch();
    const hourlyCaller = useFetch();
    const utmCaller = useFetch();

    const hasAccess = hasRole(user, RoleType.OWNER) && hasFeature("ANALYTICS_INSIGHTS");

    const shortName = selectedRestaurant?.restaurantShortName ?? "";

    useEffect(() => {
        if (selectedRestaurant && hasAccess) {
            fetchAll();
        }
    }, [selectedRestaurant]);

    async function fetchAll() {
        await Promise.all([
            fetchPageViews(),
            fetchMenuClicks(),
            fetchHourly(),
            fetchUtmSources(),
        ]);
    }

    async function fetchPageViews() {
        const url = getUserStatistics.replace(":restaurantShortName", shortName);
        const res = await pageViewCaller.call<undefined, DailyScreenViews>("GET", url);
        if (res.ok && res.data) setPageViewData(res.data);
        else snackbar.showMessage(t("statistics.error_fetching_user_statistics"), "error");
    }

    async function fetchMenuClicks() {
        const url = getMenuItemStatistics.replace(":restaurantShortName", shortName);
        const res = await menuClickCaller.call<undefined, MenuItemClicks>("GET", url);
        if (res.ok && res.data) setMenuClickData(res.data);
        else snackbar.showMessage(t("statistics.error_fetching_menu_item_statistics"), "error");
    }

    async function fetchHourly() {
        const url = getHourlyStatistics.replace(":restaurantShortName", shortName);
        const res = await hourlyCaller.call<undefined, HourlyViews>("GET", url);
        if (res.ok && res.data) setHourlyData(res.data);
    }

    async function fetchUtmSources() {
        const url = getUtmSourceStatistics.replace(":restaurantShortName", shortName);
        const res = await utmCaller.call<undefined, UtmSources>("GET", url);
        if (res.ok && res.data) setUtmData(res.data);
    }

    const redirectToPayment = () => {
        navigate(paymentSettingsRoute.path.replace(':lang', lang || 'en'));
    };

    if (!hasAccess && !hasRole(user, RoleType.OWNER)) {
        return (
            <PageContainer>
                <PageTitleCard>
                    <PageTitleText>{t("statistics.statistics")}</PageTitleText>
                </PageTitleCard>
                <div className="flex flex-col items-center justify-center mt-20 gap-4">
                    <p className="text-lg mb-4">{t("statistics.permission_denied")}</p>
                </div>
            </PageContainer>
        );
    }

    const isPreview = !hasAccess;
    const pageViewsForPeriod = isPreview ? DUMMY_PAGE_VIEWS[period] : (pageViewData?.[period] ?? []);
    const prevKey = period === 'last7days' ? 'prev7days' : 'prev30days';
    const prevPageViewsForPeriod = isPreview ? (DUMMY_PAGE_VIEWS[prevKey] ?? []) : (pageViewData?.[prevKey] ?? []);
    const menuClicksForPeriod = isPreview ? [] : (menuClickData?.[period] ?? []);
    const mainLoading = !isPreview && (pageViewCaller.loading || menuClickCaller.loading);

    return (
        <PageContainer>
            <PageTitleCard>
                <div className="flex items-center justify-between">
                    <PageTitleText>{t("statistics.statistics")}</PageTitleText>
                    {!isPreview && (
                        <div className="flex items-center gap-2">
                            <PrimaryButtonGroup
                                variant="outlined"
                                fullWidthOnMobile={false}
                                buttons={[
                                    { label: t("statistics.last_7_days"), onClick: () => setPeriod('last7days'), isSelected: period === 'last7days' },
                                    { label: t("statistics.last_30_days"), onClick: () => setPeriod('last30days'), isSelected: period === 'last30days' },
                                ]}
                            />
                            <IconButton onClick={fetchAll} title={t("common.refresh")} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </div>
                    )}
                </div>
            </PageTitleCard>

            <div className="relative">
                {isPreview && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-sm bg-white/70 rounded-lg gap-4 min-h-[400px]">
                        <LockIcon style={{ fontSize: 40 }} className="text-primary-dark opacity-60" />
                        <p className="text-base text-primary-dark font-medium text-center max-w-xs px-4">
                            {t("statistics.statistics_feature_not_available")}
                        </p>
                        <PrimaryButton variant="primary" onClick={redirectToPayment}>
                            {t('statistics.change_subscription_plan')}
                        </PrimaryButton>
                    </div>
                )}

                <div className={`mt-4 flex flex-col gap-4 mb-4 ${isPreview ? 'pointer-events-none select-none' : ''}`}>

                    {/* KPI Cards */}
                    <KpiCards
                        pageViews={pageViewsForPeriod}
                        prevPageViews={prevPageViewsForPeriod}
                        menuItemClicks={menuClicksForPeriod}
                        period={period}
                        loading={mainLoading}
                    />

                    {/* Traffic chart + UTM sources — 2-col on desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Paper elevation={2} className="p-4 md:col-span-2">
                            <div className="pb-3 pt-1 text-xl font-bold text-primary-dark">
                                {t("statistics.user_traffic")}
                            </div>
                            {!isPreview && pageViewCaller.loading ? (
                                <BigLoader />
                            ) : (
                                <UserTrafficChart
                                    data={isPreview ? DUMMY_PAGE_VIEWS : (pageViewData ?? { last7days: [], last30days: [] })}
                                    period={period}
                                />
                            )}
                        </Paper>

                        <Paper elevation={2} className="p-4">
                            <div className="pb-3 pt-1 text-xl font-bold text-primary-dark">
                                {t("statistics.utm_sources")}
                            </div>
                            {!isPreview && utmCaller.loading ? (
                                <BigLoader />
                            ) : (
                                <UtmSourceChart data={isPreview ? DUMMY_UTM_SOURCES : utmData} period={period} />
                            )}
                        </Paper>
                    </div>

                    {/* Hourly traffic */}
                    <Paper elevation={2} className="p-4">
                        <div className="pb-3 pt-1 text-xl font-bold text-primary-dark">
                            {t("statistics.hourly_traffic")}
                        </div>
                        {!isPreview && hourlyCaller.loading ? (
                            <BigLoader />
                        ) : (
                            <HourlyTrafficChart data={isPreview ? DUMMY_HOURLY_VIEWS : hourlyData} period={period} />
                        )}
                    </Paper>

                    {!isPreview && (
                        <>
                            {/* Menu item clicks */}
                            <Paper elevation={2} className="p-4">
                                <ClickCountTable period={period} externalData={menuClickData} loading={menuClickCaller.loading} />
                            </Paper>

                            {/* Ratings */}
                            <Paper elevation={2} className="p-4">
                                <Grid container>
                                    <Grid item xs>
                                        <div className="pb-4 pt-1 text-xl font-bold text-primary-dark">
                                            {t("statistics.menu_item_ratings")}
                                        </div>
                                    </Grid>
                                </Grid>
                                <MenuLikeStatisticTable />
                            </Paper>
                        </>
                    )}
                </div>
            </div>
        </PageContainer>
    );
};

export default StatisticsPage;
